import { Modal, Button, Form, DatePicker, Input } from 'antd';
import { useContext } from 'react';
import { useState } from 'react';
import { IProcess } from '../../interfaces/Workflow';
import GraphqlService from '../../services/graphql/GraphqlService';
import './NewProcessModal.less';
import { ContextApp } from '../../contexts/ContextApp';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Query } from '../../services/graphql/query';
import { CloseOutlined } from '@ant-design/icons';
import { notificationContext } from '../../contexts/NotificationContext';

interface NewProcessModalProps {
  data: {
    open: boolean;
    data: IProcess[] | null;
  };
  onCancel?: () => void;
  // procesos: IProcess[] | null;
  tenantId: string | undefined;
  user: string | undefined;
  onSuccess: () => void;
}

interface FormValues {
  reason?: string;
  date?: dayjs.Dayjs;
}

dayjs.extend(utc);

const NewProcessModal = (props: NewProcessModalProps) => {
  const { openNotification } = useContext(notificationContext);
  const { data, onCancel, tenantId, user, onSuccess } = props;
  const { Mutation, customRequest } = GraphqlService();
  const { t } = useContext(ContextApp);
  const [selectedProcess, setSelectedProcess] = useState<IProcess | null>(null);
  const [step, setStep] = useState<number>(0);
  const [form] = Form.useForm();

  const resetModal = () => {
    form.resetFields();
    setSelectedProcess(null);
    setStep(0);
    onCancel?.();
  };

  const handleStartProcess = async (values: FormValues) => {
    if (step === 1) {
      try {
        await customRequest({
          mutation: Mutation.startWorkflowInstance,
          variables: {
            input: {
              processKey: selectedProcess?.id,
              nextUser: user,
              tenantId,
              reason: values?.reason ?? '',
              processInitiatorUser: user,
              date: values?.date?.utc().toISOString() ?? '',
            },
          },
        }).then(async (response: any) => {
          try {
            await customRequest({
              query: Query.getActiveActivitiesByInstance,
              variables: {
                input: {
                  processInstanceId: response?.id,
                },
              },
            }).then(async (response: any) => {
              openNotification({
                type: 'success',
                msj: 'Proceso iniciado con éxito.',
                context: 'NewProcessModal.handleStartProcess',
              });

              if (values.date) {
                const utcStringDate = values.date.utc().toISOString();
                try {
                  await customRequest({
                    mutation: Mutation.setDueDateToTask,
                    variables: {
                      input: {
                        taskId: response?.[0]?.id,
                        dueDate: utcStringDate,
                        tenantId,
                      },
                    },
                  });
                } catch (error) {
                  openNotification({
                    type: 'error',
                    msj: 'Ocurrió un error al intentar setear la fecha de vencimiento para la primer actividad.',
                    context: 'NewProcessModal.handleStartProcess',
                  });
                }
              }
              onSuccess();
              resetModal();
              return response;
            });
          } catch (error) {
            openNotification({
              type: 'error',
              msj: 'Ha ocurrido un error al intentar iniciar un nuevo flujo de trabajo.',
              context: 'error.startWorkflowInstance',
            });
          }
        });
      } catch (error) {
        openNotification({
          type: 'error',
          msj: 'Ha ocurrido un error al intentar iniciar un nuevo flujo de trabajo.',
          context: 'error.startWorkflowInstance',
        });
      }
    }
  };

  return (
    <Modal
      open={data.open}
      onCancel={() => {
        form.resetFields();
        setSelectedProcess(null);
        onCancel?.();
      }}
      title={'Nuevo'}
      footer={null}
    >
      <h5 style={{ paddingTop: 10 }}>
        {!step ? 'Seleccioná el flujo a iniciar' : `${selectedProcess?.name}`}
      </h5>
      {!selectedProcess ? (
        <>
          <div className="cards-container-newProcess">
            {data.data?.map((process) => (
              <div
                key={process.id}
                onClick={() => {
                  setSelectedProcess(process);
                  setStep(1);
                }}
                className="card-newProcess"
              >
                <span className="name">{process.name}</span>
                <p className="description">{process.descripcion}</p>
              </div>
            ))}
          </div>
        </>
      ) : (
        <Form form={form} layout="vertical">
          {step === 1 && (
            <>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <label
                  style={{
                    width: 100,
                    marginRight: 4,
                    fontSize: '14px',
                  }}
                >
                  {t('entity.reason')}:
                </label>
                <Form.Item style={{ marginTop: 12 }} name="reason">
                  <Input
                    type="text"
                    placeholder="Ej. compra de uniformes"
                    className="custom-placeholder"
                    maxLength={50}
                  />
                </Form.Item>
              </div>
              <p style={{ fontSize: 12, color: 'gray', marginTop: '-10px' }}>
                Este nombre te ayudará a identificar tu solicitud.
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <label
                  style={{
                    width: 100,
                    marginRight: 4,
                    fontSize: '14px',
                  }}
                >
                  {t('entity.limitDate')}:
                </label>
                <Form.Item
                  name="date"
                  style={{ marginTop: 12, width: 200 }}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        return value.isBefore(dayjs().startOf('day'))
                          ? Promise.reject(
                              new Error('La fecha no puede ser anterior a hoy'),
                            )
                          : Promise.resolve();
                      },
                    },
                  ]}
                >
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: 220 }}
                    disabledDate={(current) => {
                      return current && current < dayjs().startOf('day');
                    }}
                    placeholder="Opcional"
                    allowClear={true}
                    clearIcon={<CloseOutlined />}
                  />
                </Form.Item>
              </div>

              <div className="footer-container ">
                <span
                  className="material-symbols-outlined info-icon"
                  translate="no"
                >
                  info
                </span>
                <span
                  className="footer-text"
                  style={{ fontWeight: 400, fontSize: 12, marginLeft: 8 }}
                >
                  Al iniciar un flujo, se generarán automáticamente las
                  actividades asociadas.
                </span>
              </div>

              <Form.Item>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                  }}
                >
                  <Button onClick={onCancel}>{t('action.cancel')}</Button>
                  {step === 1 && (
                    <Button
                      type="primary"
                      className="init-button"
                      htmlType="submit"
                      onClick={async () => {
                        try {
                          await form.validateFields();
                          const values = form.getFieldsValue();
                          handleStartProcess(values);
                        } catch (err) {}
                      }}
                    >
                      Iniciar
                    </Button>
                  )}
                </div>
              </Form.Item>
            </>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default NewProcessModal;
